// src/pages/modals/ConteoTabacoModal.js
//
// v2.18 · Apertura con base editable desde cierre anterior o blanco
//         – En apertura respeta detalleTabacoEntrega recibido desde CajaModal.
//         – En cierre carga entrega, reposiciones, ventas y esperado.
//         – Fórmula cierre: entregados + reposición - vendidos = esperado.
//         – Compatible con ventasTababoTotal typo anterior y ventasTabacoTotal correcto.
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Stack,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Inventory2Outlined as InventoryIcon,
  History as HistoryIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';

import api from '../../api/api';
import PinConfirmModal from './PinConfirmModal';

function normalizarDetalleTabaco(tab) {
  if (typeof tab !== 'object' || tab === null || Array.isArray(tab)) return {};

  const out = {};

  for (const [k, v] of Object.entries(tab)) {
    if (!/^\d+$/.test(String(k))) continue;

    const qty = parseInt(v, 10);
    out[String(k)] = Number.isNaN(qty) || qty < 0 ? 0 : qty;
  }

  return out;
}

function sumarDetalle(tab) {
  const clean = normalizarDetalleTabaco(tab);

  return Object.values(clean).reduce((sum, v) => {
    const n = parseInt(v, 10);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

function getProductBarcode(product) {
  return (
    product?.codigo_barras ||
    product?.codigo_barra ||
    product?.codigoBarras ||
    product?.codigoBarra ||
    product?.barcode ||
    product?.bar_code ||
    product?.ean ||
    product?.upc ||
    ''
  );
}

function productLabel(product) {
  if (!product) return 'Producto';
  return product.nombre || product.name || `Producto #${product.id}`;
}

function productSku(product) {
  return product?.sku || product?.codigo || '';
}

function getBaseSubtitle({ mode, tabacoBaseOrigen, ultimoCierreTabaco }) {
  if (mode === 'cerrar') return 'Conteo físico final para cierre de caja.';

  if (tabacoBaseOrigen === 'ultimo_cierre') {
    if (ultimoCierreTabaco?.disponible) {
      return `Base tomada del cierre de caja #${ultimoCierreTabaco.caja_id}. Puedes editar antes de abrir.`;
    }

    return 'No se encontró cierre anterior. Puedes iniciar el conteo manualmente.';
  }

  if (tabacoBaseOrigen === 'blanco') {
    return 'Conteo de apertura iniciado en blanco. Agrega los productos que correspondan.';
  }

  return 'Conteo de apertura editable.';
}

export default function ConteoTabacoModal({
  open,
  mode,
  cajaId,
  detalleTabacoEntrega,
  ventasTababoTotal,
  ventasTabacoTotal,
  tabacoBaseOrigen,
  ultimoCierreTabaco,
  onConfirm,
  onCancel,
}) {
  // — Datos principales —
  const [detalleEntrega, setDetalleEntrega] = useState({});
  const [detalleVendido, setDetalleVendido] = useState({});
  const [reposiciones, setReposiciones] = useState([]);
  const [reposProductMap, setReposProductMap] = useState({});
  const [inventoryItems, setInventoryItems] = useState([]);

  // — Búsqueda y selección —
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);

  // — Snackbar —
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // — Escáner por teclado —
  const scanBufferRef = useRef('');

  // — PIN supervisor interno, útil si este modal se usa fuera de CajaModal —
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pendingDetalle, setPendingDetalle] = useState(null);
  const [pendingTotals, setPendingTotals] = useState(null);

  const entregaPropNormalizada = useMemo(
    () => normalizarDetalleTabaco(detalleTabacoEntrega || {}),
    [detalleTabacoEntrega]
  );

  const totalEntregados = useMemo(() => sumarDetalle(detalleEntrega), [detalleEntrega]);
  const totalVendidos = useMemo(() => sumarDetalle(detalleVendido), [detalleVendido]);

  const totalVentasTabaco = Number(
    ventasTabacoTotal ??
      ventasTababoTotal ??
      0
  );

  const reposSum = useMemo(() => {
    const map = {};

    reposiciones.forEach((r) => {
      const reposicion = normalizarDetalleTabaco(r?.reposicion || {});

      Object.entries(reposicion).forEach(([pid, qty]) => {
        map[pid] = (map[pid] || 0) + (parseInt(qty, 10) || 0);
      });
    });

    return map;
  }, [reposiciones]);

  const totalReposiciones = useMemo(() => sumarDetalle(reposSum), [reposSum]);

  const totalEsperado = useMemo(() => {
    return inventoryItems.reduce((sum, it) => {
      const id = String(it.product?.id || '');
      const ent = parseInt(detalleEntrega[id], 10) || 0;
      const rep = parseInt(reposSum[id], 10) || 0;
      const ven = parseInt(detalleVendido[id], 10) || 0;

      return sum + Math.max(0, ent + rep - ven);
    }, 0);
  }, [inventoryItems, detalleEntrega, reposSum, detalleVendido]);

  const totalReal = useMemo(() => {
    return inventoryItems.reduce((sum, it) => {
      return sum + (parseInt(it.actualStock, 10) || 0);
    }, 0);
  }, [inventoryItems]);

  const diferenciaTotal = useMemo(() => totalReal - totalEsperado, [totalReal, totalEsperado]);

  const baseSubtitle = useMemo(
    () => getBaseSubtitle({ mode, tabacoBaseOrigen, ultimoCierreTabaco }),
    [mode, tabacoBaseOrigen, ultimoCierreTabaco]
  );

  const buildItemsFromMaps = useCallback(async ({
    entregaMap = {},
    vendidoMap = {},
    repos = [],
    forceActualFromEntrega = false,
  }) => {
    const cleanEntrega = normalizarDetalleTabaco(entregaMap);
    const cleanVendido = normalizarDetalleTabaco(vendidoMap);

    const reposMap = {};
    repos.forEach((r) => {
      const repo = normalizarDetalleTabaco(r?.reposicion || {});
      Object.entries(repo).forEach(([pid, qty]) => {
        reposMap[pid] = (reposMap[pid] || 0) + (parseInt(qty, 10) || 0);
      });
    });

    const allIds = Array.from(
      new Set([
        ...Object.keys(cleanEntrega),
        ...Object.keys(cleanVendido),
        ...Object.keys(reposMap),
      ])
    );

    if (!allIds.length) {
      setReposProductMap({});
      setInventoryItems([]);
      return;
    }

    const mapaProd = {};

    await Promise.all(
      allIds.map(async (id) => {
        try {
          const { data } = await api.get(`/inventario_productos/${id}/detalles`);
          const producto = data?.producto || data?.prod || data?.data || data || {};

          if (producto?.id) {
            mapaProd[String(producto.id)] = producto;
          } else {
            mapaProd[String(id)] = {
              id: Number(id),
              nombre: `Producto #${id}`,
              sku: '',
            };
          }
        } catch {
          mapaProd[String(id)] = {
            id: Number(id),
            nombre: `Producto #${id}`,
            sku: '',
          };
        }
      })
    );

    setReposProductMap(mapaProd);

    const items = allIds.map((id) => {
      const ent = parseInt(cleanEntrega[id], 10) || 0;
      const rep = parseInt(reposMap[id], 10) || 0;
      const ven = parseInt(cleanVendido[id], 10) || 0;
      const esperado = Math.max(0, ent + rep - ven);

      return {
        product: mapaProd[String(id)] || {
          id: Number(id),
          nombre: `Producto #${id}`,
          sku: '',
        },
        actualStock: forceActualFromEntrega ? ent : esperado,
      };
    });

    setInventoryItems(items);
  }, []);

  // Carga inicial
  useEffect(() => {
    if (!open) {
      setDetalleEntrega({});
      setDetalleVendido({});
      setReposiciones([]);
      setReposProductMap({});
      setInventoryItems([]);
      setSearchQuery('');
      setSuggestions([]);
      setSelectedProduct(null);
      setModalOpen(false);
      setQuantity(1);
      setPinModalOpen(false);
      setPendingDetalle(null);
      setPendingTotals(null);
      scanBufferRef.current = '';
      return;
    }

    setSearchQuery('');
    setSuggestions([]);
    setSelectedProduct(null);
    setModalOpen(false);
    setQuantity(1);
    setPinModalOpen(false);
    setPendingDetalle(null);
    setPendingTotals(null);
    scanBufferRef.current = '';

    const load = async () => {
      setLoadingInitial(true);

      try {
        if (mode === 'abrir') {
          const entregaMap = normalizarDetalleTabaco(entregaPropNormalizada);

          setDetalleEntrega(entregaMap);
          setDetalleVendido({});
          setReposiciones([]);

          await buildItemsFromMaps({
            entregaMap,
            vendidoMap: {},
            repos: [],
            forceActualFromEntrega: true,
          });

          return;
        }

        if (mode === 'cerrar') {
          if (!cajaId) {
            setSnackbar({
              open: true,
              message: 'No se recibió ID de caja para cargar el cierre.',
              severity: 'error',
            });
            return;
          }

          const { data } = await api.get(`/pos_cajas/${cajaId}/estado`);

          const entregaMap = normalizarDetalleTabaco(data?.detalle_tabaco_entrega || {});
          const vendidoMap = normalizarDetalleTabaco(data?.detalle_tabaco_vendido || {});
          const repos = Array.isArray(data?.detalle_tabaco_reposiciones)
            ? data.detalle_tabaco_reposiciones
            : [];

          setDetalleEntrega(entregaMap);
          setDetalleVendido(vendidoMap);
          setReposiciones(repos);

          await buildItemsFromMaps({
            entregaMap,
            vendidoMap,
            repos,
            forceActualFromEntrega: false,
          });
        }
      } catch (err) {
        console.error('Error cargando datos de tabaco:', err);
        setSnackbar({
          open: true,
          message: 'No se pudo cargar datos de cigarrillos',
          severity: 'error',
        });
      } finally {
        setLoadingInitial(false);
      }
    };

    load();
  }, [
    open,
    mode,
    cajaId,
    entregaPropNormalizada,
    buildItemsFromMaps,
  ]);

  // Escaneo de código de barras
  const handleBarcodeScan = useCallback(async (code) => {
    const cleanCode = String(code || '').trim();
    if (!cleanCode) return;

    const found = inventoryItems.find((item) => {
      const p = item.product || {};
      return (
        getProductBarcode(p) === cleanCode ||
        productSku(p) === cleanCode ||
        String(p.id || '') === cleanCode
      );
    });

    if (found) {
      setSelectedProduct(found.product);
      setQuantity(found.actualStock);
      setModalOpen(true);
      return;
    }

    try {
      const res = await api.get('/inventario_productos', {
        params: { search: cleanCode, limit: 1, page: 1 },
      });

      const productos = res.data.productos || [];

      if (productos.length === 1) {
        setSelectedProduct(productos[0]);
        setQuantity(0);
        setModalOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: `Código ${cleanCode} no encontrado`,
          severity: 'warning',
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: 'Error al buscar escaneo',
        severity: 'error',
      });
    }
  }, [inventoryItems]);

  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      if (
        modalOpen ||
        pinModalOpen ||
        e.ctrlKey ||
        e.altKey ||
        e.metaKey
      ) {
        return;
      }

      if (e.key === 'Enter') {
        const code = scanBufferRef.current;
        scanBufferRef.current = '';
        handleBarcodeScan(code);
      } else if (e.key.length === 1) {
        scanBufferRef.current += e.key;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, modalOpen, pinModalOpen, handleBarcodeScan]);

  // Búsqueda manual
  const handleSearch = async (e) => {
    e?.preventDefault();

    if (!searchQuery.trim()) {
      setSnackbar({
        open: true,
        message: 'Ingresa un término de búsqueda.',
        severity: 'warning',
      });
      return;
    }

    setLoadingSearch(true);

    try {
      const res = await api.get('/inventario_productos', {
        params: { search: searchQuery.trim(), limit: 50, page: 1 },
      });

      const productos = res.data.productos || [];

      if (productos.length === 1) {
        setSelectedProduct(productos[0]);
        setQuantity(0);
        setModalOpen(true);
        setSuggestions([]);
      } else {
        setSuggestions(productos);
      }
    } catch {
      setSnackbar({
        open: true,
        message: 'Error buscando productos.',
        severity: 'error',
      });
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSelectSuggestion = (prod) => {
    setSuggestions([]);
    setSelectedProduct(prod);
    setQuantity(0);
    setModalOpen(true);
  };

  const handleAddItem = () => {
    if (!selectedProduct?.id) {
      setSnackbar({
        open: true,
        message: 'Selecciona un producto válido.',
        severity: 'warning',
      });
      return;
    }

    const qty = parseInt(quantity, 10);

    if (Number.isNaN(qty) || qty < 0) {
      setSnackbar({
        open: true,
        message: 'Cantidad inválida.',
        severity: 'warning',
      });
      return;
    }

    setInventoryItems((prev) => {
      const exists = prev.find((i) => String(i.product.id) === String(selectedProduct.id));

      if (exists) {
        return prev.map((i) =>
          String(i.product.id) === String(selectedProduct.id)
            ? { ...i, product: selectedProduct, actualStock: qty }
            : i
        );
      }

      return [...prev, { product: selectedProduct, actualStock: qty }];
    });

    setDetalleEntrega((prev) => {
      if (mode !== 'abrir') return prev;

      return {
        ...prev,
        [String(selectedProduct.id)]: qty,
      };
    });

    setModalOpen(false);
    setSelectedProduct(null);
    setQuantity(1);
    setSearchQuery('');

    setSnackbar({
      open: true,
      message: 'Item agregado.',
      severity: 'success',
    });
  };

  const handleRemoveItem = (idx) => {
    setInventoryItems((prev) => {
      const item = prev[idx];

      if (mode === 'abrir' && item?.product?.id) {
        setDetalleEntrega((old) => {
          const next = { ...old };
          delete next[String(item.product.id)];
          return next;
        });
      }

      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleQuantityChange = (idx, value) => {
    const qty = parseInt(value, 10);
    const safeQty = Number.isNaN(qty) || qty < 0 ? 0 : qty;

    setInventoryItems((prev) => {
      const copy = [...prev];
      const current = copy[idx];

      copy[idx] = {
        ...current,
        actualStock: safeQty,
      };

      if (mode === 'abrir' && current?.product?.id) {
        setDetalleEntrega((old) => ({
          ...old,
          [String(current.product.id)]: safeQty,
        }));
      }

      return copy;
    });
  };

  const handleResetConteo = () => {
    if (mode === 'abrir') {
      setDetalleEntrega({});
      setInventoryItems([]);
      setSuggestions([]);
      setSearchQuery('');
      return;
    }

    setInventoryItems((prev) =>
      prev.map((item) => {
        const id = String(item.product?.id || '');
        const ent = parseInt(detalleEntrega[id], 10) || 0;
        const rep = parseInt(reposSum[id], 10) || 0;
        const ven = parseInt(detalleVendido[id], 10) || 0;
        const exp = Math.max(0, ent + rep - ven);

        return {
          ...item,
          actualStock: exp,
        };
      })
    );
  };

  const buildDetalleAndTotals = () => {
    const detalle = {};
    let totalRecibidos = 0;
    let totalMonetario = 0;

    inventoryItems.forEach(({ product, actualStock }) => {
      if (!product?.id) return;

      const qty = parseInt(actualStock, 10) || 0;

      detalle[String(product.id)] = qty;
      totalRecibidos += qty;

      const precio = Number(product.precio_neto ?? product.precio ?? product.precio_bruto ?? 0);
      totalMonetario += precio * qty;
    });

    const totals = {
      total_productos_recibidos: totalRecibidos,
      total_productos: inventoryItems.length,
      total_monetario: totalMonetario,
    };

    return { detalle, totals };
  };

  const handleSend = () => {
    if (!inventoryItems.length) {
      setSnackbar({
        open: true,
        message: 'Agrega al menos un producto al conteo.',
        severity: 'warning',
      });
      return;
    }

    const { detalle, totals } = buildDetalleAndTotals();

    if (mode === 'cerrar') {
      setPendingDetalle(detalle);
      setPendingTotals(totals);
      setPinModalOpen(true);
      return;
    }

    onConfirm(detalle, totals);
  };

  const handlePinVerified = () => {
    setPinModalOpen(false);
    onConfirm(pendingDetalle, pendingTotals);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const title = mode === 'abrir'
    ? 'Conteo de Cigarrillos - Apertura'
    : 'Conteo de Cigarrillos - Cierre';

  return (
    <>
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {baseSubtitle}
            </Typography>
          </Box>

          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {loadingInitial ? (
            <Box
              sx={{
                height: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ md: 'center' }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={900}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      {mode === 'abrir' && tabacoBaseOrigen === 'ultimo_cierre' ? (
                        <HistoryIcon fontSize="small" />
                      ) : (
                        <InventoryIcon fontSize="small" />
                      )}
                      Resumen del conteo
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {mode === 'cerrar'
                        ? 'Entregado + reposición - vendido = esperado.'
                        : 'El total real será guardado como entrega inicial de la caja.'}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                    <Chip size="small" variant="outlined" label={`Entregados: ${totalEntregados}`} />

                    {mode === 'cerrar' && (
                      <>
                        <Chip size="small" variant="outlined" label={`Repos.: ${totalReposiciones}`} />
                        <Chip size="small" variant="outlined" label={`Vendidos: ${totalVendidos || totalVentasTabaco}`} />
                        <Chip size="small" variant="outlined" label={`Esperado: ${totalEsperado}`} />
                        <Chip
                          size="small"
                          color={diferenciaTotal === 0 ? 'success' : diferenciaTotal < 0 ? 'warning' : 'error'}
                          variant={diferenciaTotal === 0 ? 'filled' : 'outlined'}
                          label={`Dif.: ${diferenciaTotal}`}
                        />
                      </>
                    )}

                    <Chip size="small" color="primary" variant="outlined" label={`Real: ${totalReal}`} />
                  </Stack>
                </Stack>

                {mode === 'abrir' && tabacoBaseOrigen === 'ultimo_cierre' && ultimoCierreTabaco?.disponible && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Base cargada desde cierre de caja #{ultimoCierreTabaco.caja_id} con{' '}
                    <strong>{Number(ultimoCierreTabaco.total_cigarrillos_cierre || 0).toLocaleString('es-CL')}</strong>{' '}
                    unidades. Puedes editar cantidades antes de confirmar.
                  </Alert>
                )}

                {mode === 'abrir' && tabacoBaseOrigen === 'blanco' && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Apertura de cigarrillos en blanco. Agrega manualmente los productos y cantidades iniciales.
                  </Alert>
                )}
              </Paper>

              {mode === 'cerrar' && reposiciones.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
                    Reposiciones
                  </Typography>

                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Productos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reposiciones.map((r, i) => {
                        const fecha = r?.fecha ? new Date(r.fecha).toLocaleString('es-CL') : '—';
                        const repo = normalizarDetalleTabaco(r?.reposicion || {});

                        const lista = Object.entries(repo)
                          .map(([pid, qty]) => `${productLabel(reposProductMap[pid]) || pid} × ${qty}`)
                          .join(', ');

                        return (
                          <TableRow key={`${r?.fecha || 'repo'}-${i}`}>
                            <TableCell>{fecha}</TableCell>
                            <TableCell>{r?.usuario || '—'}</TableCell>
                            <TableCell>{lista || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}

              <Box component="form" onSubmit={handleSearch} sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <TextField
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar producto por nombre, SKU o código de barras"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button type="submit" variant="contained" disabled={loadingSearch}>
                  {loadingSearch ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                </Button>
              </Box>

              {suggestions.length > 0 && (
                <Table sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map((prod) => (
                      <TableRow key={prod.id}>
                        <TableCell>{productLabel(prod)}</TableCell>
                        <TableCell>{productSku(prod) || '—'}</TableCell>
                        <TableCell>{getProductBarcode(prod) || '—'}</TableCell>
                        <TableCell align="center">
                          <Button onClick={() => handleSelectSuggestion(prod)}>Seleccionar</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Registrar cantidad física</DialogTitle>

                <DialogContent>
                  {selectedProduct && (
                    <Box sx={{ mb: 2 }}>
                      <Typography>
                        <strong>{productLabel(selectedProduct)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        SKU: {productSku(selectedProduct) || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Código: {getProductBarcode(selectedProduct) || '—'}
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    label="Cantidad"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0 }}
                    autoFocus
                  />
                </DialogContent>

                <DialogActions>
                  <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
                  <Button variant="contained" onClick={handleAddItem} startIcon={<SaveIcon />}>
                    Agregar
                  </Button>
                </DialogActions>
              </Dialog>

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3, mb: 1 }}>
                <Typography variant="h6" fontWeight={900}>
                  Items contados
                </Typography>

                <Tooltip title={mode === 'abrir' ? 'Limpiar conteo' : 'Restaurar valores esperados'}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<ResetIcon />}
                      onClick={handleResetConteo}
                      disabled={!inventoryItems.length}
                    >
                      {mode === 'abrir' ? 'Limpiar' : 'Restaurar esperado'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              {inventoryItems.length === 0 ? (
                <Alert severity="info">
                  No hay items en el conteo. Busca o escanea productos para agregarlos.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Entregado</TableCell>
                      <TableCell align="right">Repos.</TableCell>
                      <TableCell align="right">Vendido</TableCell>
                      <TableCell align="right">Esperado</TableCell>
                      <TableCell align="right">Real</TableCell>
                      <TableCell align="right">Dif.</TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {inventoryItems.map((item, idx) => {
                      const id = String(item.product?.id || '');
                      const ent = parseInt(detalleEntrega[id], 10) || 0;
                      const rep = parseInt(reposSum[id], 10) || 0;
                      const ven = parseInt(detalleVendido[id], 10) || 0;
                      const exp = Math.max(0, ent + rep - ven);
                      const real = parseInt(item.actualStock, 10) || 0;
                      const dif = real - exp;

                      return (
                        <TableRow key={id || idx}>
                          <TableCell>{productLabel(item.product)}</TableCell>
                          <TableCell>{productSku(item.product) || '—'}</TableCell>
                          <TableCell align="right">{ent}</TableCell>
                          <TableCell align="right">{rep}</TableCell>
                          <TableCell align="right">{ven}</TableCell>
                          <TableCell align="right">{mode === 'cerrar' ? exp : '—'}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={item.actualStock}
                              onChange={(e) => handleQuantityChange(idx, e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {mode === 'cerrar' ? (
                              <Chip
                                size="small"
                                label={dif}
                                color={dif === 0 ? 'success' : dif < 0 ? 'warning' : 'error'}
                                variant={dif === 0 ? 'filled' : 'outlined'}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton color="error" onClick={() => handleRemoveItem(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={loadingInitial || inventoryItems.length === 0}
            startIcon={<SaveIcon />}
          >
            {mode === 'abrir' ? 'Confirmar apertura' : 'Confirmar cierre'}
          </Button>
        </DialogActions>
      </Dialog>

      <PinConfirmModal
        open={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        onVerified={handlePinVerified}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

ConteoTabacoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['abrir', 'cerrar']),
  cajaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detalleTabacoEntrega: PropTypes.object,
  ventasTababoTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ventasTabacoTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tabacoBaseOrigen: PropTypes.oneOf(['blanco', 'ultimo_cierre', 'apertura']),
  ultimoCierreTabaco: PropTypes.shape({
    disponible: PropTypes.bool,
    caja_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fecha_cierre: PropTypes.string,
    detalle_tabaco_cierre: PropTypes.object,
    total_cigarrillos_cierre: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConteoTabacoModal.defaultProps = {
  mode: 'abrir',
  cajaId: null,
  detalleTabacoEntrega: {},
  ventasTababoTotal: 0,
  ventasTabacoTotal: 0,
  tabacoBaseOrigen: 'blanco',
  ultimoCierreTabaco: null,
};